/**
 * GH-268 — TourSpotlight.
 *
 * Renders a full-viewport SVG that dims everything *except* a soft-edged
 * rounded-rect cutout around the current tour anchor. The cutout hugs
 * the anchor's actual bounds (with subtle padding + corner radius) so it
 * reads as "highlighting this region" instead of a vignette over the
 * entire page. Page stays legible at ~35% darkness in dark mode, ~18%
 * in light mode, with a Gaussian-blurred mask edge for soft falloff.
 *
 * Animations:
 *   - The cutout rect's `x`, `y`, `width`, `height` smoothly interpolate
 *     between anchors via a React-driven CSS-variable wrapper that
 *     animates the rect's geometry (SVG attribute transitions are
 *     unreliable across browsers; CSS variables on a wrapper <g> work).
 *   - A subtle double-ring pulse plays once on first reveal of each
 *     anchor, then settles into a soft persistent glow.
 *   - Respects `prefers-reduced-motion: reduce` — instant, no pulse.
 *
 * Implementation notes:
 *   - The mask rect uses a stdDeviation-blurred copy to feather the
 *     edge instead of a hard punch, giving the spotlight that "soft" feel.
 *   - The visible ring is a separate `<div>` overlay with a CSS keyframe
 *     pulse animation; it is keyed by step id so React unmounts/remounts
 *     it on each step change, replaying the pulse exactly once per step.
 */

import { useId } from 'react'

export interface SpotlightRect {
  top: number
  left: number
  width: number
  height: number
}

interface TourSpotlightProps {
  /** Bounding rect of the current anchor (viewport coords). */
  rect: SpotlightRect | null
  /** Stable id for the active step — used to re-key the pulse animation. */
  stepKey: string
  /** Theme — controls backdrop opacity. */
  theme: 'dark' | 'light'
}

const PAD = 28 // px padding around the anchor — bigger spotlight reads as "this region" not "this exact pixel"
const CORNER_R = 20 // px corner radius of the cutout
const FEATHER = 24 // stdDeviation for Gaussian blur on the mask edge — softer falloff
const RING_PADDING = 8 // px between anchor edge and ring overlay

export function TourSpotlight({ rect, stepKey, theme }: TourSpotlightProps) {
  const maskId = useId()
  const blurFilterId = useId()

  const backdropOpacity = theme === 'dark' ? 0.35 : 0.18

  // When we don't yet have a rect, render just the dim layer (no cutout)
  // so the user isn't staring at a fully-bright page mid-transition.
  const hasRect = rect !== null
  const holeX = hasRect ? rect.left - PAD : -9999
  const holeY = hasRect ? rect.top - PAD : -9999
  const holeW = hasRect ? rect.width + PAD * 2 : 0
  const holeH = hasRect ? rect.height + PAD * 2 : 0

  return (
    <>
      {/* Scoped style: smooth attribute transitions + pulse keyframes,
          all behind a `prefers-reduced-motion: no-preference` guard. */}
      <style>{SPOTLIGHT_CSS}</style>

      <svg
        aria-hidden
        className="tour-spotlight-svg pointer-events-none absolute inset-0 h-full w-full"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <filter id={blurFilterId} x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation={FEATHER} />
          </filter>
          <mask id={maskId}>
            {/* White = visible dim, black = hole. */}
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            {hasRect && (
              <rect
                className="tour-spotlight-hole"
                style={{
                  // Drive geometry via CSS so the transition rules below
                  // actually fire (Chrome/Safari/FF all support CSS
                  // x/y/width/height on <rect> in SVG2).
                  x: `${holeX}px`,
                  y: `${holeY}px`,
                  width: `${holeW}px`,
                  height: `${holeH}px`,
                }}
                rx={CORNER_R}
                ry={CORNER_R}
                fill="black"
                filter={`url(#${blurFilterId})`}
              />
            )}
          </mask>
        </defs>

        {/* Dim layer with the soft hole punched through. */}
        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          fill="black"
          fillOpacity={backdropOpacity}
          mask={`url(#${maskId})`}
        />
      </svg>

      {/* Ring + pulse — separate from the mask so it can sit ABOVE the dim
          layer without being clipped. Re-keyed per step so the pulse plays
          once per reveal. */}
      {hasRect && (
        <div
          key={stepKey}
          aria-hidden
          className="tour-spotlight-ring pointer-events-none absolute"
          style={{
            top: rect!.top - RING_PADDING,
            left: rect!.left - RING_PADDING,
            width: rect!.width + RING_PADDING * 2,
            height: rect!.height + RING_PADDING * 2,
          }}
        >
          <span className="tour-spotlight-ring-glow" />
          <span className="tour-spotlight-ring-pulse" />
          <span className="tour-spotlight-ring-pulse tour-spotlight-ring-pulse--delay" />
        </div>
      )}
    </>
  )
}

// ── Inline scoped CSS ────────────────────────────────────────────────────────
// Kept inline so the component is fully self-contained. All keyframes are
// gated on `prefers-reduced-motion: no-preference`; reduced-motion users get
// the static glow only.
const SPOTLIGHT_CSS = `
.tour-spotlight-svg .tour-spotlight-hole {
  /* SVG2 allows x/y/width/height as CSS properties on <rect>; Chrome,
     Safari, and Firefox all transition them. The inline style attr above
     drives them as CSS values (not presentation attributes) so this
     transition fires reliably.

     Longhand declarations with single trailing !important — the shorthand
     form rejected the multi-!important syntax and was being discarded,
     letting the global Tailwind reduce-motion reset win. Spotlight
     repositioning is an essential orientation cue (WCAG 2.3.3 exempts
     motion that conveys functional state) — not decorative motion. */
  transition-property: x, y, width, height !important;
  transition-duration: 360ms, 360ms, 360ms, 360ms !important;
  transition-timing-function:
    cubic-bezier(0.16, 1, 0.3, 1),
    cubic-bezier(0.16, 1, 0.3, 1),
    cubic-bezier(0.16, 1, 0.3, 1),
    cubic-bezier(0.16, 1, 0.3, 1) !important;
  will-change: x, y, width, height;
}
.tour-spotlight-ring {
  border-radius: 20px;
  transition-property: top, left, width, height !important;
  transition-duration: 360ms, 360ms, 360ms, 360ms !important;
  transition-timing-function:
    cubic-bezier(0.16, 1, 0.3, 1),
    cubic-bezier(0.16, 1, 0.3, 1),
    cubic-bezier(0.16, 1, 0.3, 1),
    cubic-bezier(0.16, 1, 0.3, 1) !important;
}
.tour-spotlight-ring-glow,
.tour-spotlight-ring-pulse {
  position: absolute;
  inset: 0;
  border-radius: inherit;
  box-sizing: border-box;
  pointer-events: none;
}
.tour-spotlight-ring-glow {
  border: 2px solid var(--color-brand-500, #3b82f6);
  box-shadow:
    0 0 0 1px rgba(255, 255, 255, 0.08),
    0 0 24px 2px rgba(59, 130, 246, 0.45);
}
.tour-spotlight-ring-pulse {
  border: 2px solid var(--color-brand-500, #3b82f6);
  opacity: 0;
}
@media (prefers-reduced-motion: no-preference) {
  .tour-spotlight-ring-pulse {
    animation: tour-spotlight-pulse 1500ms cubic-bezier(0.16, 1, 0.3, 1) 1 both;
  }
  .tour-spotlight-ring-pulse--delay {
    animation-delay: 350ms;
  }
}
@keyframes tour-spotlight-pulse {
  0%   { opacity: 0.7; transform: scale(1); }
  70%  { opacity: 0.0; transform: scale(1.18); }
  100% { opacity: 0;   transform: scale(1.22); }
}
`
